/**
 * Iter130 · Widget «Mi asistencia WhatsApp»
 * ---------------------------------------------
 * Se renderiza dentro de MyWork y muestra:
 *   - Estado del fichado de HOY (canal, hora, geocerca)
 *   - Últimos N mensajes WhatsApp del operador (bidireccional)
 *   - Botón para copiar el número al que hay que mandar el WhatsApp
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChatCircleDots, CheckCircle, WarningCircle, Clock, MapPin, ArrowClockwise } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const GEOFENCE_LABEL = {
  inside: { icon: CheckCircle, cls: "text-emerald-700", label: "Dentro de obra" },
  outside: { icon: WarningCircle, cls: "text-rose-700", label: "Fuera de obra" },
  location_missing: { icon: MapPin, cls: "text-amber-700", label: "Sin ubicación" },
  no_geofence_configured: { icon: MapPin, cls: "text-slate-500", label: "Geocerca no configurada" },
};

export default function MyWhatsAppPanel() {
  const [state, setState] = useState({ loading: true, employee: null, events: [], today: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/hr/me/whatsapp-events?limit=15");
      setState({
        loading: false,
        employee: data.employee,
        events: data.events || [],
        today: data.today || null,
      });
    } catch (e) {
      // 404 = el usuario no está vinculado a un HR → panel oculto
      const status = e?.response?.status;
      if (status !== 404) toast.error(apiError(e));
      setState({ loading: false, employee: null, events: [], today: null });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!state.employee && !state.loading) return null;   // hide para no-HR
  if (state.loading) return null;

  const t = state.today;
  const meta = GEOFENCE_LABEL[t?.geofence_status] || null;
  const Ico = meta?.icon || Clock;
  const wa = state.employee?.whatsapp || state.employee?.phone;

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-4 space-y-3"
             data-testid="my-whatsapp-panel">
      <div className="flex items-center gap-2">
        <ChatCircleDots size={18} weight="fill" className="text-emerald-700" />
        <h3 className="font-heading font-black text-slate-900">Mi asistencia · WhatsApp</h3>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={load}
                data-testid="my-wa-refresh" className="h-7 gap-1 text-[11px]">
          <ArrowClockwise size={12} weight="bold" /> Recargar
        </Button>
      </div>

      {t ? (
        <div className={`rounded-md border p-3 flex items-center gap-3 ${t.status === "present" ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}
             data-testid="my-wa-today">
          <Ico size={22} weight="fill" className={meta?.cls || "text-slate-500"} />
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Fichado de hoy</p>
            <p className="text-sm text-slate-900 font-semibold">
              {t.status === "present" ? "Presente" : (t.status || "—").toUpperCase()}
              {t.marked_at && <span className="text-slate-500 font-normal"> · {(t.marked_at || "").slice(11, 16)}</span>}
              {t.channel && <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-white uppercase">{t.channel}</span>}
            </p>
            {meta && (
              <p className="text-[11px] text-slate-600 mt-0.5">
                {meta.label}
                {typeof t.geofence_distance_m === "number" && ` · ${Math.round(t.geofence_distance_m)} m del sitio`}
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="my-wa-today-empty">
          Aún no registras entrada hoy. Manda <b>ENTRADA</b> por WhatsApp adjuntando tu ubicación.
        </div>
      )}

      <div className="text-[11px] text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
        <span>Tu WhatsApp registrado: <b className="font-mono">{wa || "(no cargado)"}</b></span>
        <span>Comandos: <b>ENTRADA</b> · <b>SALIDA</b> · adjunta ubicación 📍</span>
      </div>

      <div className="border border-slate-200 rounded overflow-hidden">
        <div className="bg-slate-100 px-2 py-1 text-[10px] uppercase tracking-widest font-bold text-slate-600">
          Últimos mensajes ({state.events.length})
        </div>
        {state.events.length === 0 ? (
          <p className="p-3 text-[12px] italic text-slate-500">Sin mensajes registrados.</p>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
            {state.events.map((e, i) => (
              <li key={e.message_sid || i} className="p-2 text-[11.5px] flex flex-col">
                <div className="flex gap-2 items-center">
                  <span className="font-mono text-slate-500 text-[10px] whitespace-nowrap">
                    {(e.received_at || "").slice(5, 16).replace("T", " ")}
                  </span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${e.status === "ok" ? "bg-emerald-100 text-emerald-800" : e.status === "outbound_reminder" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"}`}>
                    {e.action || e.status}
                  </span>
                  <span className="text-slate-700 truncate flex-1">{e.body || "—"}</span>
                </div>
                {e.reply && <p className="pl-14 text-slate-500 italic mt-0.5 truncate">↳ {e.reply}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
